import { Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLazyLoadQuery } from 'react-relay';
import { Image } from "@lecartte/shared-ui/components/Image";
import LogoLecartte from '@/assets/images/logo.svg';
import { appConfig } from '@/config/app';

const LECARTTE_CLIENT_URL = process.env.LECARTTE_CLIENT_URL || 'http://localhost:3015';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator
} from "@lecartte/shared-ui/components/DropdownMenu";
import { usePlanModal } from '@/features/subscription/contexts/PlanModalContext';
import { PendingExcessChargesQuery } from '@/features/subscription/graphql/PendingExcessChargesQuery';
import type { PendingExcessChargesQuery as PendingExcessChargesQueryType } from '@/__generated__/PendingExcessChargesQuery.graphql';

const useHasPendingExcess = () => {
  const data = useLazyLoadQuery<PendingExcessChargesQueryType>(PendingExcessChargesQuery, {}, { fetchPolicy: 'store-and-network' });
  return data.myExcessCharges?.some((c) => c.computedState === 'INVOICED' || c.computedState === 'OVERDUE') ?? false;
};

const ExcessBadge = () => {
  const hasPending = useHasPendingExcess();
  if (!hasPending) return null;
  return (
    <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
  );
};

const ExcessMenuBadge = () => {
  const hasPending = useHasPendingExcess();
  if (!hasPending) return null;
  return (
    <span className="absolute -right-1 -top-1 flex h-2 w-2 rounded-full bg-red-500" />
  );
};

type AppHeaderProps = {
  onLogout: () => void;
};

export const AppHeader = ({ onLogout }: AppHeaderProps) => {
  const navigate = useNavigate();
  const { openPlanModal } = usePlanModal();

  return (
    <header className="relative z-10 border-b border-slate-200/70 bg-[var(--surface)]/85 backdrop-blur">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-6 py-4">
        <a href={LECARTTE_CLIENT_URL} className="flex items-center gap-3">
          <Image
            src={LogoLecartte}
            alt={appConfig.name}
            width={100}
            height={44}
            priority
            className="h-[44px] w-[100px] rounded-xl object-cover shadow-sm"
          />
        </a>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Configurações"
              className="relative flex items-center justify-center rounded-full border border-slate-200 bg-white/80 p-2 text-slate-600 transition hover:border-[var(--color-secondary-soft)] hover:text-[var(--color-secondary)]"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <Suspense fallback={null}>
                <ExcessBadge />
              </Suspense>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8} className="w-52">
            <DropdownMenuItem onSelect={() => navigate('/restaurante/minha-conta')}>
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>Minha Conta</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/restaurante/meus-cartoes')}>
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
              </svg>
              <span>Meus Cartões</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => openPlanModal()}>
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              <span>Assinatura</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/restaurante/cobrancas-excedentes')}>
              <div className="relative">
                <svg viewBox="0 0 24 24" className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                </svg>
                <Suspense fallback={null}>
                  <ExcessMenuBadge />
                </Suspense>
              </div>
              <span>Cobranças excedentes</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onLogout}>
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Sair</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
